import React, {FC} from 'react';
import {connect} from 'react-redux';

import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {makeGetContactsManagerSearchState} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerSearchTabsEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {
  contactsManagerSearchPageCleanUpAction,
  contactsManagerSearchPageTabSelectedAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/search/contactsManagerSearchActions';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {SearchPage} from './searchPage';

/*
 * Props.
 */

interface SearchPageContainerPublicProps {
  panelId: ContactsManagerPanelId;
}

type SearchPageContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type SearchPageContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type SearchPageContainerProps = SearchPageContainerPublicProps &
  SearchPageContainerStateProps &
  SearchPageContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetSelectedTab(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector([getContactsManagerSearchState], (searchState) => {
    // If we are in a bad state, we can just default to the contacts tab.
    if (!searchState) {
      return ContactsManagerSearchTabsEnum.CONTACTS;
    }
    return searchState.selectedTab;
  });
}

function makeGetSelectedCardIds(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.contactsTableState?.selectedCardIds ?? [],
  );
}

/*
 * Component.
 */

const SearchPageContainerComponent: FC<SearchPageContainerProps> = (props) => {
  const {panelId, selectedTab, selectedCardIds, onCleanUp, onTabSelect} = props;

  // Clean up the search state on unmount.
  useInitialEffect(() => () => {
    onCleanUp();
  });

  return (
    <SearchPage
      panelId={panelId}
      selectedTab={selectedTab}
      selectedCardIds={selectedCardIds}
      onTabSelect={onTabSelect}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SearchPageContainerPublicProps>()(() => {
  const getContactsManagerSearchState = makeGetContactsManagerSearchState();
  return {
    selectedTab: makeGetSelectedTab(getContactsManagerSearchState),
    selectedCardIds: makeGetSelectedCardIds(getContactsManagerSearchState),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<SearchPageContainerPublicProps>()(({panelId}) => ({
  onTabSelect: (tab: ContactsManagerSearchTabsEnum) =>
    contactsManagerSearchPageTabSelectedAction(panelId, tab),
  onCleanUp: () => contactsManagerSearchPageCleanUpAction(panelId),
}));

export const SearchPageContainer = connect(mapStateToProps, mapDispatchToProps)(SearchPageContainerComponent);
